import { FormikHandlers, FormikProps } from 'formik';
import React from 'react';
/**
 * @fileoverview
 * A higher order component that returns a valid FormRenderer component
 * that can be used with our Formik setup.
 *
 * Basic form fields:
 * - text
 * - email
 *
 * Advanced form fields:
 * - select company
 *
 * Please add more field types as needed.
 */

import { TextField } from 'src/components/TextField';
import { SelectCompany } from 'src/features/forms/renderer/SelectCompany/SelectCompany';

const getFieldName = (key: string | number) => {
  return String(key);
};

export function withGenericFormFields<Fields extends Record<string, unknown>>({
  containerClassName = '',
  prefix,
  fields,
}: {
  containerClassName?: string;
  prefix?: React.ReactNode;
  fields: {
    label: React.ReactNode;
    name: keyof Fields;
    type: 'company' | 'text' | 'email';
    disabled?: boolean;
    className?: string;
    onChange?: (
      // The event
      e: React.ChangeEvent,
      // Formik props
      props: FormikProps<Fields>,
      // Formik's handleChange
      handleChange: FormikHandlers['handleChange'],
    ) => void;
  }[];
}): {
  (props: FormikProps<Fields>): JSX.Element;
} {
  return (props: FormikProps<Fields>) => {
    const { errors, handleBlur, handleChange, setFieldValue, touched, values } =
      props;
    return (
      <div className={containerClassName}>
        {prefix && <>{prefix}</>}
        {fields.map((field, idx) => {
          // we don't know how to handle symbols in field names
          if (typeof field.name === 'symbol') {
            console.error('Field name is a symbol', field.name);
            return null;
          }

          const fieldName = getFieldName(field.name);

          if (field.type === 'text' || field.type === 'email') {
            const fieldError = errors[field.name];
            const hasError = Boolean(touched[field.name] && fieldError);
            return (
              <div key={fieldName}>
                <TextField
                  autoFocus={idx === 0}
                  disabled={field.disabled}
                  className={field.className}
                  error={hasError}
                  fullWidth
                  // The empty space makes it so the height of the text
                  // field doesn't change when the error shows up.
                  helperText={
                    touched[field.name] && typeof fieldError === 'string'
                      ? fieldError
                      : ' '
                  }
                  label={field.label}
                  id={fieldName}
                  onBlur={handleBlur}
                  onChange={(e) =>
                    field.onChange?.(e, props, handleChange) ?? handleChange(e)
                  }
                  name={fieldName}
                  type={field.type}
                  value={values[field.name]}
                  variant="outlined"
                />
              </div>
            );
          }

          if (field.type === 'company') {
            return (
              <SelectCompany<Fields>
                key={fieldName}
                errors={errors}
                label={field.label}
                onChange={(selected) => {
                  setFieldValue(
                    fieldName,
                    selected?.company?.id ?? selected?.label ?? '',
                  );
                }}
                selectedCompanyId={
                  typeof values[fieldName] === 'string'
                    ? values[fieldName]
                    : undefined
                }
                touched={touched}
                className="[&_.MuiFormLabel-root]:whitespace-normal"
              />
            );
          }

          return null;
        })}
      </div>
    );
  };
}
